import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import { getMessages, sendMessage } from '@/api/messages';
import { queryKeys } from '@/lib/queryKeys';
import { showError } from '@/lib/toast';
import type {
  SendMessageRequest,
  MessageSummary,
  GetMessagesResponse,
  ListConversationsResponse,
} from '@/api/types';

/**
 * Extended message type with optimistic UI status
 */
export interface OptimisticMessage extends MessageSummary {
  _status?: 'sending' | 'failed';
  _idempotencyKey?: string;
}

/**
 * Hook to fetch messages for a conversation with infinite scroll pagination
 */
export function useMessages(conversationId: string) {
  return useInfiniteQuery({
    queryKey: queryKeys.messages.conversation(conversationId),
    queryFn: ({ pageParam }) =>
      getMessages(
        pageParam
          ? { conversationId, limit: 50, cursor: pageParam }
          : { conversationId, limit: 50 }
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.has_more ? lastPage.pagination.next_cursor ?? undefined : undefined,
    select: (data) => ({
      messages: data.pages.flatMap((page) => page.messages) as OptimisticMessage[],
      conversation: data.pages[0]?.conversation,
      hasMore: data.pages[data.pages.length - 1]?.pagination.has_more ?? false,
    }),
    enabled: !!conversationId,
  });
}

/**
 * Helper to merge a single message into the infinite query cache
 * Used by realtime and polling to avoid full refetch
 */
export function mergeMessageIntoCache(
  queryClient: QueryClient,
  conversationId: string,
  message: MessageSummary | OptimisticMessage
) {
  queryClient.setQueryData<InfiniteData<GetMessagesResponse>>(
    queryKeys.messages.conversation(conversationId),
    (old) => {
      if (!old) return old;

      // Deduplicate by ID
      const exists = old.pages.some((p) =>
        p.messages.some((m) => m.id === message.id)
      );
      if (exists) return old;

      // Add to last page (messages ordered ascending by created_at)
      return {
        ...old,
        pages: old.pages.map((page, idx) =>
          idx === old.pages.length - 1
            ? { ...page, messages: [...page.messages, message] }
            : page
        ),
      };
    }
  );
}

/**
 * Hook to send a message to a conversation with optimistic UI
 */
export function useSendMessage(conversationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: Omit<SendMessageRequest, 'conversation_id'>) =>
      sendMessage({
        conversation_id: conversationId,
        text: request.text,
        idempotency_key: request.idempotency_key,
      }),

    // Optimistic update: add temporary message immediately
    onMutate: async (newMessage) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.messages.conversation(conversationId),
      });

      // Snapshot previous value for rollback
      const previousMessages = queryClient.getQueryData<InfiniteData<GetMessagesResponse>>(
        queryKeys.messages.conversation(conversationId)
      );

      // Create optimistic message with "sending" status
      const optimisticMessage: OptimisticMessage = {
        id: `temp-${newMessage.idempotency_key}`,
        sender_type: 'operator',
        sender_operator_id: null, // Will be set by server
        sanitized_text: newMessage.text,
        created_at: new Date().toISOString(),
        _status: 'sending',
        _idempotencyKey: newMessage.idempotency_key,
      };

      // Add to cache
      mergeMessageIntoCache(queryClient, conversationId, optimisticMessage);

      return { previousMessages, optimisticId: optimisticMessage.id };
    },

    // On success: replace temp message with real one
    onSuccess: (data, _variables, context) => {
      queryClient.setQueryData<InfiniteData<GetMessagesResponse>>(
        queryKeys.messages.conversation(conversationId),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              messages: page.messages.map((msg) =>
                msg.id === context?.optimisticId
                  ? {
                      ...msg,
                      id: data.message_id,
                      _status: undefined,
                      _idempotencyKey: undefined,
                    }
                  : msg
              ),
            })),
          };
        }
      );
      // Update conversation's last_message_at in cache (avoid full refetch)
      queryClient.setQueriesData<InfiniteData<ListConversationsResponse>>(
        { queryKey: queryKeys.conversations.all },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              conversations: page.conversations.map((conv) =>
                conv.id === conversationId
                  ? { ...conv, last_message_at: data.created_at, message_count: conv.message_count + 1 }
                  : conv
              ),
            })),
          };
        }
      );
    },

    // On error: mark message as failed (allow retry)
    onError: (_err, _variables, context) => {
      showError('Failed to send message. Click "Retry" to try again.');
      queryClient.setQueryData<InfiniteData<GetMessagesResponse>>(
        queryKeys.messages.conversation(conversationId),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              messages: page.messages.map((msg) =>
                msg.id === context?.optimisticId
                  ? { ...msg, _status: 'failed' as const }
                  : msg
              ),
            })),
          };
        }
      );
    },
  });
}
